import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { courseAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, Lock, BookOpen, GraduationCap,
  Loader2, ChevronRight, Award, Send, ChevronDown, ChevronUp, Menu, X,
} from 'lucide-react';

// === SIDEBAR MODULE LIST (Desktop) ===
function ModuleSidebar({ modules, currentModule, onSelect, isOpen, onToggle }) {
  return (
    <>
      {/* Mobile toggle */}
      <button onClick={onToggle} className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-800/90 backdrop-blur text-white" data-testid="sidebar-toggle">
        {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed md:sticky top-0 left-0 h-screen w-72 bg-slate-900 border-r border-white/5 overflow-y-auto z-40 transition-transform md:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="module-sidebar">
        <div className="p-4 border-b border-white/5">
          <h2 className="text-sm font-bold text-white">Formação PAREI-AD</h2>
          <p className="text-xs text-gray-500 mt-1">13 Módulos · Curso Profissional</p>
        </div>
        <nav className="p-2">
          {modules.map((m) => {
            const isCompleted = m.status === 'completed';
            const isLocked = m.status === 'locked';
            const isActive = currentModule === m.module_num;
            return (
              <button
                key={m.module_num}
                disabled={isLocked}
                onClick={() => { onSelect(m.module_num); onToggle(); }}
                className={`w-full text-left p-3 rounded-lg mb-1 flex items-center gap-3 transition-all text-sm ${
                  isActive ? 'bg-purple-600/20 border border-purple-500/30' :
                  isCompleted ? 'hover:bg-green-500/5' :
                  isLocked ? 'opacity-40 cursor-not-allowed' :
                  'hover:bg-white/5'
                }`}
                data-testid={`sidebar-module-${m.module_num}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                  isCompleted ? 'bg-green-500/20 text-green-400' :
                  isActive ? 'bg-purple-600 text-white' :
                  isLocked ? 'bg-white/5 text-gray-600' :
                  'bg-white/10 text-gray-300'
                }`}>
                  {isCompleted ? <Check className="h-3.5 w-3.5" /> : isLocked ? <Lock className="h-3 w-3" /> : m.module_num}
                </div>
                <span className={`truncate ${isActive ? 'text-purple-300 font-medium' : isCompleted ? 'text-green-400' : isLocked ? 'text-gray-600' : 'text-gray-300'}`}>
                  {m.title}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={onToggle} />}
    </>
  );
}

// === CONTENT AREA ===
function ModuleContent({ moduleData, onStartQuiz }) {
  return (
    <div data-testid="module-content-area">
      {/* Video placeholder */}
      <div className="rounded-xl overflow-hidden mb-6 aspect-video flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
        <div className="text-center">
          <BookOpen className="h-12 w-12 text-purple-400/50 mx-auto mb-2" />
          <p className="text-sm text-purple-400/70">Vídeo em breve</p>
        </div>
      </div>

      {/* Module text content */}
      <div className="rounded-xl p-6 mb-6 text-sm leading-relaxed text-gray-300 whitespace-pre-wrap break-words [overflow-wrap:anywhere]" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="module-text">
        {moduleData.content}
      </div>

      {/* CTA to quiz */}
      {!moduleData.is_completed && (
        <button onClick={onStartQuiz} className="w-full py-4 rounded-xl text-base font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }} data-testid="start-quiz-btn">
          <GraduationCap className="h-5 w-5" />
          Fazer Prova do Módulo {moduleData.module_num}
        </button>
      )}
      {moduleData.is_completed && (
        <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
          <Check className="h-6 w-6 text-green-400 mx-auto mb-1" />
          <p className="text-sm text-green-300 font-medium">Módulo concluído!</p>
        </div>
      )}
    </div>
  );
}

// === QUIZ STEPPER (1 question per screen) ===
function QuizView({ moduleData, onSubmit, onBack, submitting }) {
  const questions = moduleData.quiz_questions || [];
  const isCompleted = moduleData.is_completed;
  const savedAnswers = moduleData.submission?.answers || [];

  const initAnswers = {};
  savedAnswers.forEach(a => { initAnswers[a.question_number] = a.answer; });

  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(initAnswers);
  const [validationErrors, setValidationErrors] = useState([]);

  const question = questions[currentQ];
  if (!question) return null;

  const totalQ = questions.length;
  const answeredCount = Object.values(answers).filter(a => a && a.length >= 50).length;
  const currentAnswer = answers[question.number] || '';
  const pct = Math.round(((currentQ + 1) / totalQ) * 100);
  const isCurrentValid = currentAnswer.length >= 50;

  const handleChange = (value) => {
    setAnswers(prev => ({ ...prev, [question.number]: value }));
    setValidationErrors(prev => prev.filter(n => n !== question.number));
  };

  const goNext = () => { if (currentQ < totalQ - 1) setCurrentQ(c => c + 1); };
  const goPrev = () => { if (currentQ > 0) setCurrentQ(c => c - 1); };

  const handleSubmit = () => {
    // Validate all answers
    const errors = [];
    for (const q of questions) {
      const ans = answers[q.number] || '';
      if (ans.trim().length < 50) errors.push(q.number);
    }
    if (errors.length > 0) {
      setValidationErrors(errors);
      setCurrentQ(questions.findIndex(q => q.number === errors[0]));
      toast.error(`${errors.length} questão(ões) precisam de respostas mais completas (mín. 50 caracteres)`);
      return;
    }

    const formatted = Object.entries(answers).map(([qNum, text]) => ({
      question_number: parseInt(qNum),
      answer: text,
    }));
    onSubmit(formatted);
  };

  const isLast = currentQ === totalQ - 1;
  const hasError = validationErrors.includes(question.number);

  return (
    <div data-testid="quiz-view">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-300" data-testid="quiz-back-btn"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <p className="text-xs text-purple-400 font-medium">Prova — Módulo {moduleData.module_num}</p>
          <p className="text-xs text-gray-500">{answeredCount}/{totalQ} respondidas (mín. 50 caracteres)</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Questão {currentQ + 1} de {totalQ}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' }} animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }} />
        </div>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div key={currentQ} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }} className="mb-6">
          <div className={`rounded-xl p-5 ${hasError ? 'border border-red-500/50' : ''}`} style={{ background: 'rgba(255,255,255,0.03)', border: hasError ? undefined : '1px solid rgba(255,255,255,0.06)' }} data-testid={`quiz-q-${question.number}`}>
            <p className="text-xs text-purple-400 font-medium mb-2">Questão {question.number}</p>
            <p className="text-sm text-gray-200 mb-4 leading-relaxed">{question.text}</p>
            <textarea
              value={currentAnswer}
              onChange={(e) => handleChange(e.target.value)}
              disabled={isCompleted}
              placeholder="Escreva sua resposta de forma dissertativa (mínimo 50 caracteres)..."
              className="w-full h-40 p-4 rounded-xl text-sm bg-white/5 border border-white/10 text-white placeholder:text-gray-600 resize-none focus:outline-none focus:border-purple-500/50 transition-colors disabled:opacity-60"
              data-testid={`quiz-answer-${question.number}`}
            />
            <div className="flex justify-between mt-2">
              <span className={`text-xs ${currentAnswer.length >= 50 ? 'text-green-400' : 'text-gray-600'}`}>
                {currentAnswer.length}/50 caracteres {currentAnswer.length >= 50 && <Check className="h-3 w-3 inline" />}
              </span>
              {hasError && <span className="text-xs text-red-400">Resposta muito curta</span>}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={goPrev} disabled={currentQ === 0} className="flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-30 transition-all" data-testid="quiz-prev">
          <ArrowLeft className="h-4 w-4" /> Anterior
        </button>
        <div className="flex-1" />
        {isLast ? (
          !isCompleted && (
            <button onClick={handleSubmit} disabled={submitting || answeredCount < totalQ} className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }} data-testid="submit-quiz-final">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Concluir Módulo ({answeredCount}/{totalQ})
            </button>
          )
        ) : (
          <button onClick={goNext} className="flex items-center gap-1 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }} data-testid="quiz-next">
            Próxima <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Question dots */}
      <div className="flex justify-center gap-1 flex-wrap">
        {questions.map((q, i) => {
          const hasAnswer = (answers[q.number] || '').length >= 50;
          const isCurrent = i === currentQ;
          const hasErr = validationErrors.includes(q.number);
          return (
            <button key={i} onClick={() => setCurrentQ(i)} className={`w-7 h-7 rounded-full text-xs font-medium transition-all ${isCurrent ? 'ring-2 ring-purple-400' : ''} ${hasErr ? 'bg-red-500/30 text-red-300' : hasAnswer ? 'bg-green-600 text-white' : 'bg-white/5 text-gray-600 hover:bg-white/10'}`} data-testid={`quiz-dot-${i}`}>
              {hasAnswer ? <Check className="h-3 w-3 mx-auto" /> : i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// === CERTIFICATE VIEW ===
function CertificateView({ cert, userName }) {
  return (
    <div className="text-center py-8 max-w-lg mx-auto" data-testid="certificate-view">
      <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
        <GraduationCap className="h-10 w-10 text-white" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-4">Certificação PAREI-AD</h1>
      <div className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(245,158,11,0.3)' }}>
        <p className="text-amber-400 text-xs uppercase tracking-wider mb-3">Certificamos que</p>
        <p className="text-xl font-bold text-white mb-4">{userName}</p>
        <p className="text-sm text-gray-400 mb-2">Completou com êxito a</p>
        <p className="text-sm font-semibold text-purple-300 mb-1">FORMAÇÃO PROFISSIONAL PAREI-AD / U-CLEAN</p>
        <p className="text-xs text-gray-500 mb-4">Método Enantiodromia · 13 Módulos</p>
        <p className="text-xs text-gray-600">Emitido em {cert?.issued_at ? new Date(cert.issued_at).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')}</p>
      </div>
    </div>
  );
}

// === MAIN PAGE ===
export default function CourseProfessionalPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingModule, setLoadingModule] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState('content'); // content | quiz | certificate
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedModuleNum, setSelectedModuleNum] = useState(null);

  const loadModules = useCallback(async () => {
    try {
      const res = await courseAPI.getProfessionalModules();
      setModules(res.data.modules);
      setProgress(res.data);
      if (res.data.course_completed) {
        const certRes = await courseAPI.getProfessionalCertificate();
        if (certRes.data.certified) setCertificate(certRes.data);
      }
      // Auto-select current module
      if (!selectedModuleNum) {
        setSelectedModuleNum(res.data.current_module || 1);
      }
    } catch (err) {
      toast.error('Erro ao carregar módulos');
    } finally {
      setLoading(false);
    }
  }, [selectedModuleNum]);

  useEffect(() => {
    if (!authLoading && user) loadModules();
  }, [authLoading, user, loadModules]);

  // Load module content when selection changes
  useEffect(() => {
    if (selectedModuleNum && !loading) loadModuleContent(selectedModuleNum);
  }, [selectedModuleNum, loading]);

  const loadModuleContent = async (num) => {
    setLoadingModule(true);
    try {
      const res = await courseAPI.getProfessionalModule(num);
      setModuleData(res.data);
      setView('content');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar módulo');
    } finally {
      setLoadingModule(false);
    }
  };

  const handleSelectModule = (num) => {
    setSelectedModuleNum(num);
  };

  const handleSubmitQuiz = async (answers) => {
    if (!moduleData) return;
    setSubmitting(true);
    try {
      const res = await courseAPI.submitProfessionalQuiz(moduleData.module_num, answers);
      toast.success(`Módulo ${moduleData.module_num} concluído! +${res.data.tokens_awarded} AWA`);
      if (res.data.course_completed) {
        toast.success('Parabéns! Certificação PAREI-AD emitida!', { duration: 6000 });
      }
      await loadModules();
      // Move to next module
      if (res.data.next_module <= 13) {
        setSelectedModuleNum(res.data.next_module);
      } else {
        setView('certificate');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar respostas');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#06061a' }}>
        <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#06061a' }} data-testid="course-professional-page">
      {/* Sidebar */}
      <ModuleSidebar
        modules={modules}
        currentModule={selectedModuleNum}
        onSelect={handleSelectModule}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main content area */}
      <main className="flex-1 min-h-screen overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8">
          {/* Header */}
          {moduleData && (
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate('/tratamentos')} className="text-gray-500 hover:text-gray-300 hidden md:block"><ArrowLeft className="h-5 w-5" /></button>
              <div className="flex-1 ml-10 md:ml-0">
                <p className="text-xs text-purple-400 font-medium">Módulo {moduleData.module_num} de 13</p>
                <h1 className="text-lg font-bold text-white leading-tight">{moduleData.title}</h1>
              </div>
              {moduleData.is_completed && <span className="text-xs bg-green-500/15 text-green-400 px-3 py-1 rounded-full font-medium">Concluído</span>}
            </div>
          )}

          {/* Loading state */}
          {loadingModule && (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-purple-400" /></div>
          )}

          {/* Content or Quiz */}
          {!loadingModule && moduleData && view === 'content' && (
            <ModuleContent moduleData={moduleData} onStartQuiz={() => setView('quiz')} />
          )}

          {!loadingModule && moduleData && view === 'quiz' && (
            <QuizView moduleData={moduleData} onSubmit={handleSubmitQuiz} onBack={() => setView('content')} submitting={submitting} />
          )}

          {view === 'certificate' && certificate && (
            <CertificateView cert={certificate} userName={user?.name} />
          )}
        </div>
      </main>
    </div>
  );
}
